import type { FormWithProcesses } from '@/api/processStepsApi'
import { newestPerFamily } from './processFormUsage'
import { versionLabel } from './versioning'

/** The minimum a process field must expose to be named and told apart. */
export interface NameableProcessField {
  id: string
  label: string
}

/** Stand-in for a process field with no label at all. */
export const UNTITLED_PROCESS = 'Untitled'

/** Hint for a process no form embeds — the orphan case, worth saying out loud. */
export const NO_FORM_HINT = 'Not in any form'

/**
 * How many forms to name before summarising the rest as `+N`.
 *
 * One. This is a qualifier riding beside the name it qualifies, in a dropdown
 * row and a table cell — at two it grew long enough to push the process name
 * into an ellipsis, which inverted the whole point: the name is the primary
 * information and the hint exists to serve it.
 */
const MAX_FORMS_IN_HINT = 1

/** A process field's display name. Never empty, so two blanks still collide visibly. */
export function processFieldName(field: NameableProcessField): string {
  return field.label.trim() || UNTITLED_PROCESS
}

/** `Collection V2` — named the way form versions are named everywhere else on screen. */
function formDescriptor(form: FormWithProcesses): string {
  return `${form.name} ${versionLabel(form.version)}`
}

/** Last characters of an id, as a visible tie-breaker of last resort. */
function shortId(id: string): string {
  return `#${id.replace(/[^a-zA-Z0-9]/g, '').slice(-6).toUpperCase()}`
}

/** One selectable row in the process picker — a field seen through one form. */
export interface ProcessFieldRow {
  /** Stable react key. A field appears once per form that embeds it. */
  key: string
  /** The library field this row selects. Every row for one field selects the same one. */
  fieldId: string
  name: string
  /** The form this row stands for, or null when no form embeds the field. */
  form: FormWithProcesses | null
}

/**
 * Expand process fields into one picker row per embedding form (z8nrz7cr9r).
 *
 * A single collapsed row per field made same-named processes unreadable: three
 * rows saying "Process" with a comma-joined qualifier crammed beside them. Listed
 * per form instead, each row names exactly one place the process is used, which
 * is the thing that actually identifies it. The shape mirrors the table's
 * existing "Used by" popover — form name, version on the right — so the two read
 * the same way.
 *
 * **One row per FORM, not per form definition.** Versions of a form collapse to
 * the newest, keyed on familyId — see `newestPerFamily`, which is also what the
 * page's form filter collapses on, so the filter and the picker cannot disagree
 * about what counts as one form. Real data settles the collapse itself: on dev,
 * one process ("Day 0") is embedded in 15 form definitions spanning only 5
 * distinct forms — `Processing` alone accounts for V13 down to V2. Listed per
 * definition it produced 15 rows for a single process, which is not a selector
 * any more. Per form it is 5, and 5 is the number that answers "which process is
 * this".
 *
 * The version still shows, because it identifies WHICH form: a process in
 * `Processing V13` and another in `Processing V2` are genuinely different
 * placements. The table's "Used by" popover remains the place that enumerates
 * every definition — this is identification, not an inventory.
 *
 * Source order is preserved, and the API sorts by name then version descending,
 * so the first occurrence of a name is its newest.
 *
 * **Rows are not independent configurations.** Process config is keyed on the
 * LIBRARY field, so every row for one field opens and edits the same bill of
 * materials — the rows say *where it is used*, not *what can be configured
 * separately*. The picker states this wherever a field expands to more than one
 * row, because the layout otherwise invites exactly the opposite reading.
 */
export function processFieldRows(
  fields: NameableProcessField[],
  formsByFieldId: Map<string, FormWithProcesses[]>,
): ProcessFieldRow[] {
  const rows: ProcessFieldRow[] = []
  for (const field of fields) {
    const name = processFieldName(field)
    const forms = formsByFieldId.get(field.id) ?? []
    if (forms.length === 0) {
      rows.push({ key: `${field.id}:none`, fieldId: field.id, name, form: null })
      continue
    }

    for (const form of newestPerFamily(forms)) {
      rows.push({ key: `${field.id}:${form.id}`, fieldId: field.id, name, form })
    }
  }
  return rows
}

/**
 * Which forms embed this field, or the orphan hint when none do.
 *
 * Versions of one form collapse to their newest, by familyId. `Collection V3,
 * Collection V2` spent the whole hint saying one name twice, and a human reading
 * it does not think "two forms" — they think "Collection".
 *
 * Collapsing cannot merge two processes that a version genuinely separates: a
 * process in Collection V3 and one in Collection V2 still yield different
 * descriptors. It only shortens the case where ONE process sits in several
 * versions of the same form — and because config is keyed on the library field,
 * that is the common case.
 *
 * `+N` therefore counts remaining distinct FORMS, not remaining versions, which
 * is the count that means something.
 */
function formHint(
  fieldId: string,
  formsByFieldId: Map<string, FormWithProcesses[]>,
): string {
  const forms = formsByFieldId.get(fieldId) ?? []
  if (forms.length === 0) return NO_FORM_HINT

  const distinct = newestPerFamily(forms)
  const named = distinct.slice(0, MAX_FORMS_IN_HINT).map(formDescriptor).join(', ')
  const rest = distinct.length - MAX_FORMS_IN_HINT
  return rest > 0 ? `${named} +${rest}` : named
}

/**
 * Secondary text that tells same-named processes apart (z8nrz7cr9r).
 *
 * Process config is keyed on the LIBRARY field, and nothing stops two library
 * fields carrying the same label — so the picker listed several identical rows
 * reading "Wash" and choosing between them was guesswork. Picking the wrong one
 * edits a different form's bill of materials, which is the kind of mistake that
 * is only noticed later, on the shop floor.
 *
 * What actually distinguishes two same-named processes is **where they are
 * used**, so that is the hint: the forms embedding each one. A process no form
 * embeds says so instead, which doubles as a cleanup signal.
 *
 * Three deliberate choices:
 *
 * - **Only ambiguous names get a hint.** A process whose name is already unique
 *   in the list needs no qualifier, and adding one to every row would bury the
 *   names the user is actually scanning.
 * - **Ids are the last resort, not the first.** Two fields sharing both a name
 *   AND a set of forms genuinely cannot be told apart by anything meaningful, so
 *   those — and only those — get a short id appended. Leading with ids would put
 *   an opaque token on rows a human could have distinguished by form.
 * - **Names are compared after trimming, and blanks collapse to `Untitled`.**
 *   Two fields labelled `Wash ` and `Wash` look identical on screen and must be
 *   treated as the collision they are.
 *
 * @returns fieldId → hint, containing an entry only for fields that need one.
 */
export function processFieldHints(
  fields: NameableProcessField[],
  formsByFieldId: Map<string, FormWithProcesses[]>,
): Map<string, string> {
  const idsByName = new Map<string, string[]>()
  for (const field of fields) {
    const name = processFieldName(field)
    const held = idsByName.get(name)
    if (held) held.push(field.id)
    else idsByName.set(name, [field.id])
  }

  const hints = new Map<string, string>()
  for (const ids of idsByName.values()) {
    if (ids.length < 2) continue

    const byHint = new Map<string, string[]>()
    for (const id of ids) {
      const hint = formHint(id, formsByFieldId)
      const held = byHint.get(hint)
      if (held) held.push(id)
      else byHint.set(hint, [id])
    }

    for (const [hint, sharing] of byHint) {
      // A form hint shared by two fields distinguishes neither of them, so both
      // pick up an id. One field holding a hint alone keeps it clean.
      const needsId = sharing.length > 1
      for (const id of sharing) {
        hints.set(id, needsId ? `${hint} · ${shortId(id)}` : hint)
      }
    }
  }
  return hints
}
